import { GameState } from '../types/game';
import { ScoreBreakdownEntry, ScoreComputation, computeScore } from './scoring';
import { gameRules, makeEmptyDiceMask } from './gameRules';
import { scoreKeptDiceWithOrigins } from './keptDiceScoring';

interface PotentialBreakdown {
  segments: string[];
  current: string[];
  currentDetails?: ScoreBreakdownEntry[];
}

export interface PotentialResult { points: number; scoringDice: number; breakdown: PotentialBreakdown; nonScoringKept?: number }
type ConstraintKind =
  | 'nonScoringKept'
  | 'winRequiresAllScoring'
  | 'belowThreeScoring'
  | 'firstEntryTooLow'
  | 'freeScoringUnkept';

// Centralna funkcja wyliczająca potencjał punktowy bieżącej tury ONLINE
// Zasady:
//  - Jeśli brak rolli -> null
//  - Jeśli istnieją segmenty: suma segmentów + aktualnie zaznaczone kości, które nie są jeszcze w capturedMask
//  - capturedMask chroni przed podwójnym liczeniem wcześniej utrwalonych kości
/**
 * Calculates potential score for the current online turn.
 */
export function calculatePotential(gameState: GameState): PotentialResult | null {
  const tc = gameState.turnCycle;
  if (gameState.mode === 'REAL' || !tc || tc.rollCount === 0) return null;
  const segments = gameState.segments || [];
  const committedPoints = segments.reduce((s, seg) => s + seg.points, 0);
  const committedDice = segments.reduce((s, seg) => s + seg.scoringDiceCount, 0);
  const keptMask = tc.kept || makeEmptyDiceMask();
  const capturedMask = tc.capturedMask || makeEmptyDiceMask();
  const dice = tc.dice || [];
  const lastRollIndex = typeof tc.lastRoll?.rollIndex === 'number' ? tc.lastRoll?.rollIndex : (tc.rollCount > 0 ? tc.rollCount - 1 : undefined);
  const scored = scoreKeptDiceWithOrigins({
    dice,
    keptMask,
    capturedMask,
    diceOrigins: tc.diceOrigins,
    lastRollIndex,
  });
  const keptScore: ScoreComputation = scored.score;
  const nonScoringKept = scored.nonScoringKept;
  return {
    points: committedPoints + keptScore.basePoints,
    scoringDice: committedDice + keptScore.scoringDiceCount,
    nonScoringKept,
    breakdown: {
      segments: segments.flatMap(seg => seg.breakdown),
      current: keptScore.breakdown,
      currentDetails: keptScore.breakdownDetails,
    },
  };
}

function winGuardReason(
  potential: PotentialResult,
  playerScore: number,
  lastRollScoring: number,
  keptFromLast: number,
  committedPoints: number,
  diceForWin: number[],
): string | null {
  const totalWithAllScoring = playerScore + committedPoints + computeScore(diceForWin).basePoints;
  const totalScoreAfterBank = playerScore + potential.points;
  if ((totalScoreAfterBank === gameRules.scoring.winScore || totalWithAllScoring === gameRules.scoring.winScore) && lastRollScoring > 0 && keptFromLast < lastRollScoring) {
    return 'game.bank.winRequiresAllScoring';
  }
  return null;
}

export interface BankConstraintResult { reason: string | null; kind?: ConstraintKind }

/**
 * Determines whether banking/end-turn is blocked and why.
 */
export function getBankConstraint(
  gameState: GameState,
  potential: PotentialResult | null,
  opts: { isEndTurn: boolean },
): BankConstraintResult {
  if (!potential) return { reason: null };

  const player = gameState.players[gameState.currentPlayerIndex];
  if (!player) return { reason: null };
  const hasEntry = Boolean(gameState.firstEntries?.[player.id] ?? player.hasFirstEntry);
  const tc = gameState.turnCycle;

  if (potential.nonScoringKept && potential.nonScoringKept > 0) {
    return { reason: 'game.bank.nonScoringKept', kind: 'nonScoringKept' };
  }

  if (tc?.lastRoll && typeof player.score === 'number') {
    const lastRollScoring = tc.lastRoll.scoringDiceCount || 0;
    const keptMask = tc.kept || makeEmptyDiceMask();
    const keptFromLast = (tc.lastRoll.indices || []).filter(idx => keptMask[idx]).length;
    const segments = gameState.segments || [];
    const committedPoints = segments.reduce((s, seg) => s + seg.points, 0);
    const winReason = winGuardReason(potential, player.score, lastRollScoring, keptFromLast, committedPoints, tc.lastRoll.dice || []);
    if (winReason) {
      return { reason: winReason, kind: 'winRequiresAllScoring' };
    }
  }
  if (tc && !tc.lastRoll && typeof player.score === 'number') {
    const keptMask = tc.kept || makeEmptyDiceMask();
    const capturedMask = tc.capturedMask || makeEmptyDiceMask();
    const trayDice = (tc.dice || []).filter((_, i) => !capturedMask[i]);
    const trayScoring = computeScore(trayDice);
    const keptFromTray = (tc.dice || []).filter((_, i) => !capturedMask[i] && keptMask[i]).length;
    const segments = gameState.segments || [];
    const committedPoints = segments.reduce((s, seg) => s + seg.points, 0);
    const winReason = winGuardReason(potential, player.score, trayScoring.scoringDiceCount || 0, keptFromTray, committedPoints, trayDice);
    if (winReason) {
      return { reason: winReason, kind: 'winRequiresAllScoring' };
    }
  }

  const lastRollBust = gameState.turnCycle?.lastRoll?.didBust;
  const belowThresholdScore = player.score < gameRules.scoring.minScoringDice.thresholdScore;
  const skipScoringGuard = opts.isEndTurn ? lastRollBust === true : false;
  if (!skipScoringGuard && belowThresholdScore && potential.points > 0 && potential.scoringDice < gameRules.scoring.minScoringDice.minCount) {
    return { reason: opts.isEndTurn ? 'game.bank.belowThreeScoringEndTurn' : 'game.bank.belowThreeScoringBank', kind: 'belowThreeScoring' };
  }
  if (!hasEntry && potential.points > 0 && potential.points < gameRules.scoring.firstEntryMin) {
    return { reason: opts.isEndTurn ? 'game.bank.firstEntryTooLowEndTurn' : 'game.bank.firstEntryTooLowBank', kind: 'firstEntryTooLow' };
  }
  if (opts.isEndTurn && tc && tc.dice && tc.dice.length > 0) {
    const keptMask = tc.kept || makeEmptyDiceMask();
    const capturedMask = tc.capturedMask || makeEmptyDiceMask();
    const freeDice = (tc.dice || []).filter((_, i) => !keptMask[i] && !capturedMask[i]);
    const freeScoring = computeScore(freeDice);
    if (freeScoring.scoringDiceCount > 0 && !lastRollBust) {
      return { reason: 'game.bank.freeScoringUnkept', kind: 'freeScoringUnkept' };
    }
  }
  return { reason: null };
}

/**
 * Returns a user-facing reason for disabling the bank action.
 */
export function calculateBankBlockedReason(gameState: GameState, potential: PotentialResult | null): string | null {
  return getBankConstraint(gameState, potential, { isEndTurn: false }).reason;
}

// Separate, more explicit reason for disabling button (could differ from bank scoring nullification logic)
/**
 * Returns a user-facing reason for disabling the end-turn action.
 */
export function calculateEndTurnBlockedReason(gameState: GameState, potential: PotentialResult | null): string | null {
  return getBankConstraint(gameState, potential, { isEndTurn: true }).reason;
}
